import { FormProvider } from 'react-hook-form';
import { ComponentProps } from 'react';
import {
    Box, Flex, ModalBody, Spinner,
} from '@chakra-ui/react';
import { useUpdatePropertyForm } from '../../hooks/use-update-property-form';
import { EditPropertyScreen } from './edit-property-screen';
import { ConfirmPropertyEditScreen } from './confirmation-screen';
import { useProperty, usePropertyKeyPickupLocation } from '../../hooks/use-property';
import { ReadOnlyPropertyScreen } from './read-only-property-screen';

export function EditPropertyForm({
    propertyId,
    onClose,
    onUpdatedProperty,
    showHeader = true,
    showCloseButton = true,
    p,
    enforceValidKey,
    confirmButtonText,
    showAgencyKeyFields = true,
    showOnsiteKeyFields = true,
    isReadOnly = false,
}: {
    propertyId: number;
    confirmButtonText?: string;
    onClose: () => void;
    onUpdatedProperty?: () => void;
    showHeader?: boolean;
    showCloseButton?: boolean;
    p?: ComponentProps<typeof ModalBody>['p'];
    enforceValidKey: boolean;
    showAgencyKeyFields?: boolean;
    showOnsiteKeyFields?: boolean;
    isReadOnly?: boolean;
}) {
    const {
        screen, editPropertyForm, updatePropertyDetailsMutation,
        handleSubmit, handleConfirm, handleBack, handleClose,
    } = useUpdatePropertyForm({
        propertyId,
        onClose,
        onUpdatedProperty,
        enforceValidKey,
        showAgencyKeyFields,
        showOnsiteKeyFields,
        isReadOnly,
    });
    const propertyQuery = useProperty(propertyId);
    const usePickupLocationQuery = usePropertyKeyPickupLocation(propertyId);

    if (
        propertyQuery.data === undefined
        || propertyQuery.isLoading
        || usePickupLocationQuery.data === undefined
        || usePickupLocationQuery.isLoading
    ) {
        return (
            <ModalBody p={p}>
                <Box w="full">
                    <Flex
                        alignItems="center"
                        fontWeight="bold"
                        justifyContent="center"
                        py={8}
                        px={8}
                        borderColor="muted.50"
                        borderWidth={1}
                        rounded="md"
                    >
                        <Spinner />
                    </Flex>
                </Box>
            </ModalBody>
        );
    }

    return (
        <FormProvider {...editPropertyForm}>
            {screen === 'READ_ONLY' && (
                <ReadOnlyPropertyScreen
                    propertyId={propertyId}
                    showHeader={showHeader}
                    showCloseButton={showCloseButton}
                    p={p}
                    showAgencyKeyFields={showAgencyKeyFields}
                    showOnsiteKeyFields={showOnsiteKeyFields}
                    onClose={onClose}
                />
            )}
            {screen === 'EDIT_PROPERTY_DATA' ? (
                <EditPropertyScreen
                    propertyId={propertyId}
                    onClose={handleClose}
                    onSubmit={handleSubmit}
                    showHeader={showHeader}
                    showCloseButton={showCloseButton}
                    p={p}
                    showAgencyKeyFields={showAgencyKeyFields}
                    showOnsiteKeyFields={showOnsiteKeyFields}
                />
            ) : null}
            {screen === 'CONFIRM' ? (
                <ConfirmPropertyEditScreen
                    propertyId={propertyId}
                    onSubmit={handleConfirm}
                    isLoading={updatePropertyDetailsMutation.isPending}
                    onBack={handleBack}
                    error={updatePropertyDetailsMutation.error}
                    showHeader={showHeader}
                    p={p}
                    confirmButtonText={confirmButtonText}
                    showAgencyKeyFields={showAgencyKeyFields}
                    showOnsiteKeyFields={showOnsiteKeyFields}
                />
            ) : null}
        </FormProvider>
    );
}
